import { createSlice } from "@reduxjs/toolkit";
import { type POSADeliveryWithBill } from "../../app/12_POSAManager/api";
import type LotteryV2ConfigurationItem from "@tootsweet/model/lottery/LotteryV2ConfigurationItem";
import type LotteryV2StepType from "@tootsweet/model/lottery/LotteryV2StepType";

interface BusinessUserI {
  name: string;
  phone: string;
  email: string;
  password: string;
}

export interface SaasGameConfigI {
  colors: {
    background: string;
    primary: string;
    accent: string;
    btnText?: string;
    titles?: string;
  };
  logoURL?: string | undefined;
}

export interface SaasStepCompletionI {
  cta?: boolean;
  gifts?: boolean;
  animation?: boolean;
}

export interface POSAOrderItem {
  businessUserId?: string;
  unitPrice?: number;

  name: string;
  qty: number;
  posaTypeId: string;
  priceHT?: number;
  vat?: number;
  priceTTC?: number;
}

export interface POSAOrderDeliveryInfo {
  name?: string;
  address?: string;
  phone?: string;
}

export interface POSAOrder {
  items: POSAOrderItem[];

  totalPriceHT?: number;
  totalVAT?: number;
  totalPriceTTC?: number;

  deliveryInformations?: POSAOrderDeliveryInfo;
  billAndDelivery?: POSADeliveryWithBill[];
}

export interface SaasModalPostPaymentPropsI {
  isModalPostPaymentOpen?: boolean;
  headerText: string;
  bodyText: string;
}

export interface SaasStateI {
  businessUser: BusinessUserI;
  items: LotteryV2ConfigurationItem[];
  gameConfig: SaasGameConfigI;
  stepCompletion: SaasStepCompletionI;
  googlePlaceSaas: string;
  instagramPlaceSaas: string;
  instagramAccountSaas: string;
  nCustomersPerDay: number | undefined;
  estimatedPlayersPerDay: number;
  usePercentages: boolean;
  stepType: LotteryV2StepType | undefined;
  gameURL: string;
  // TODO: rename this
  flyersOrderStep?: string;
  // TODO: rename this
  currentPOSAOrder: POSAOrder;
  creationTs?: number;
  modalPaymentProps?: SaasModalPostPaymentPropsI;
  posaOrderIsRecomputing: boolean;
  addingPOSAToBasketIds: string[];
  currency?: string;
}

export const DEFAULT_POSA_ORDER = {
  items: [],
  deliveryInformations: {
    name: "",
    address: "",
    phone: ""
  }
};

const initialState: SaasStateI = {
  businessUser: {
    name: "",
    phone: "",
    email: "",
    password: ""
  },
  stepType: undefined,
  googlePlaceSaas: "",
  instagramPlaceSaas: "",
  instagramAccountSaas: "",
  usePercentages: false,
  items: [
    { index: 0, label: "", conditions: "", percentage: 0, numberPerWeek: 0 },
    { index: 1, label: "", conditions: "", percentage: 0, numberPerWeek: 0 },
    { index: 2, label: "", conditions: "", percentage: 0, numberPerWeek: 0 }
  ],
  gameConfig: {
    logoURL: "",
    colors: {
      primary: "",
      background: "",
      accent: ""
    }
  },
  gameURL: "",
  stepCompletion: {
    cta: false,
    gifts: false,
    animation: false
  },
  currentPOSAOrder: DEFAULT_POSA_ORDER,
  nCustomersPerDay: undefined,
  estimatedPlayersPerDay: 0,
  posaOrderIsRecomputing: false,
  addingPOSAToBasketIds: []
};

const saasStatesSlice = createSlice({
  name: "saas",
  initialState,
  reducers: {
    setBusinessUser: (state, action) => {
      state.businessUser = action.payload;
    },
    setGooglePlaceSaas: (state, action) => {
      state.googlePlaceSaas = action.payload;
    },
    setInstagramPlaceSaas: (state, action) => {
      state.instagramPlaceSaas = action.payload;
    },
    setInstagramAccountSaas: (state, action) => {
      state.instagramAccountSaas = action.payload;
    },
    setStepCompletion: (state, action) => {
      state.stepCompletion = action.payload;
    },
    setGameURL: (state, action) => {
      state.gameURL = action.payload;
    },
    setFlyersOrderStep: (state, action) => {
      state.flyersOrderStep = action.payload;
    },
    setCurrentPOSAOrder: (state, action) => {
      state.currentPOSAOrder = action.payload;
    },
    setModalPaymentProps: (state, action) => {
      state.modalPaymentProps = action.payload;
    },
    setCreationTs: (state, action) => {
      state.creationTs = action.payload;
    },
    setPOSAOrderIsRecomputing: (state, action) => {
      state.posaOrderIsRecomputing = action.payload;
    },
    addPOSAToBasketStart: (state, action) => {
      if (!state.addingPOSAToBasketIds.includes(action.payload)) {
        state.addingPOSAToBasketIds.push(action.payload);
      }
    },
    addPOSAToBasketEnd: (state, action) => {
      state.addingPOSAToBasketIds = state.addingPOSAToBasketIds.filter(
        (id) => id !== action.payload
      );
    },
    setCurrency: (state, action) => {
      state.currency = action.payload;
    }
  }
});

export default saasStatesSlice.reducer;

export const {
  setFlyersOrderStep,
  setCurrentPOSAOrder,
  setModalPaymentProps,
  setPOSAOrderIsRecomputing,
  addPOSAToBasketStart,
  addPOSAToBasketEnd
} = saasStatesSlice.actions;
